<template>
  <div class="form-document-container">
    <div class="document-layout">

      <!-- RIGHT SIDEBAR: Page Thumbnails -->
      <aside class="thumbnails-sidebar" ref="thumbnailsSidebar">
        <h3 class="thumbnails-title">עמודים</h3>
        <div class="thumbnails-list">
          <div v-if="images.length < 1" class="thumbnail-item">
            <div class="thumbnail-placeholder loading"></div>
            <span class="page-badge">1</span>
          </div>
          <div
            v-for="(image, index) in images"
            :key="index"
            class="thumbnail-item"
            :ref="`thumb-${index}`"
            @click="scrollToPage(index)"
          >
            <div class="thumbnail-placeholder" :class="{ active: currentPage === index }">
              <img :src="`data:image/jpg;base64, ${image.data}`" class="thumbnail-img" alt="" />
            </div>
            <span class="page-badge" :class="{ active: currentPage === index }">{{ index + 1 }}</span>
          </div>
        </div>
      </aside>

      <!-- CENTER: Document Area -->
      <main class="document-area" ref="documentArea">
        <div v-if="images.length < 1" class="loading-overlay">
          <div class="spinner">
            <div class="double-bounce1"></div>
            <div class="double-bounce2"></div>
          </div>
        </div>
        <div class="document-scroll" v-if="images.length > 0">
          <div class="all-imgs-container">
            <div
              class="all-imgs-item"
              v-for="(image, index) in images"
              :key="index"
              :ref="`imgNumber${index + 1}`"
            >
              <img :src="`data:image/jpg;base64, ${image.data}`" alt="image" />
            </div>
          </div>
        </div>
      </main>

    </div>
  </div>
</template>

<script>
import DocumentService from "@/services/DocumentService";

export default {
  name: "FormDocument",
  data() {
    return {
      images: [],
      currentPage: 0,
    };
  },
  beforeCreate() {
    DocumentService.getTemplateImages(this.$route.params.id).then((res) => {
      this.images = res.data.images;
    });
  },
  mounted() {
    this.$nextTick(() => {
      const docArea = this.$refs.documentArea;
      if (docArea) {
        docArea.addEventListener("scroll", this.onDocumentScroll, { passive: true });
      }
    });
  },
  beforeUnmount() {
    const docArea = this.$refs.documentArea;
    if (docArea) {
      docArea.removeEventListener("scroll", this.onDocumentScroll);
    }
  },
  methods: {
    scrollToPage(index) {
      const docArea = this.$refs.documentArea;
      if (!docArea) return;
      const pageEls = docArea.querySelectorAll(".all-imgs-item");
      if (!pageEls[index]) return;
      const pageTop = pageEls[index].getBoundingClientRect().top;
      const areaTop = docArea.getBoundingClientRect().top;
      docArea.scrollTo({
        top: docArea.scrollTop + (pageTop - areaTop) - 32,
        behavior: "smooth",
      });
      this.currentPage = index;
    },

    onDocumentScroll() {
      const docArea = this.$refs.documentArea;
      if (!docArea) return;
      const pageEls = docArea.querySelectorAll(".all-imgs-item");
      if (!pageEls.length) return;
      const areaRect = docArea.getBoundingClientRect();
      const areaHeight = areaRect.height;
      let bestPage = -1;
      let bestVisible = 0;
      for (let i = 0; i < pageEls.length; i++) {
        const pageRect = pageEls[i].getBoundingClientRect();
        const visibleTop = Math.max(pageRect.top, areaRect.top);
        const visibleBottom = Math.min(pageRect.bottom, areaRect.bottom);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        if (visibleHeight > bestVisible) {
          bestVisible = visibleHeight;
          bestPage = i;
        }
      }
      if (bestPage >= 0 && bestVisible >= areaHeight * 0.7 && bestPage !== this.currentPage) {
        this.currentPage = bestPage;
        this.$nextTick(() => {
          const thumbRefs = this.$refs[`thumb-${bestPage}`];
          const thumbEl = Array.isArray(thumbRefs) ? thumbRefs[0] : thumbRefs;
          if (thumbEl) thumbEl.scrollIntoView({ behavior: "smooth", block: "nearest" });
        });
      }
    },
  },
};
</script>

<style scoped>
.form-document-container {
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  direction: rtl;
  font-family: Arial, Helvetica, sans-serif;
  background: #f6f7f8;
}

.document-layout {
  display: flex;
  flex-direction: row;
  height: 100%;
  width: 100%;
}

/* ─── Right Sidebar: Page Thumbnails ─── */

.thumbnails-sidebar {
  width: 140px;
  min-width: 140px;
  height: 100%;
  overflow-y: auto;
  background: #ffffff;
  border-left: 1px solid #e2e8f0;
  padding: 16px 10px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.thumbnails-sidebar::-webkit-scrollbar { width: 4px; }
.thumbnails-sidebar::-webkit-scrollbar-track { background: transparent; }
.thumbnails-sidebar::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 2px; }

.thumbnails-title {
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94a3b8;
  margin: 0 0 12px 0;
  text-align: center;
}

.thumbnails-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.thumbnail-item {
  position: relative;
  cursor: pointer;
}

.thumbnail-placeholder {
  width: 100%;
  aspect-ratio: 3 / 4;
  background: #f1f5f9;
  border-radius: 6px;
  border: 2px solid #e2e8f0;
  transition: border-color 0.2s;
  overflow: hidden;
  cursor: pointer;
}

.thumbnail-placeholder.active {
  border-color: #137fec;
  box-shadow: 0 0 0 3px rgba(19, 127, 236, 0.1);
}

.thumbnail-placeholder.loading {
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

.thumbnail-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

@keyframes shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

.page-badge {
  position: absolute;
  top: 5px;
  right: 5px;
  background: #64748b;
  color: white;
  font-size: 9px;
  font-weight: 700;
  padding: 1px 5px;
  border-radius: 3px;
  line-height: 1.4;
}

.page-badge.active {
  background: #137fec;
}

/* ─── Center: Document Area ─── */

.document-area {
  flex: 1;
  height: 100%;
  overflow-y: auto;
  background: #f6f7f8;
  position: relative;
}

.document-area::-webkit-scrollbar { width: 6px; }
.document-area::-webkit-scrollbar-track { background: #f1f5f9; }
.document-area::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }

.loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.document-scroll {
  padding: 32px 24px;
  display: flex;
  justify-content: center;
  min-height: 100%;
}

.all-imgs-container {
  width: 793px;
  min-width: 793px;
  max-width: 793px;
  height: auto;
  display: inline-block;
  box-shadow: 4px 6px 18px rgba(0, 0, 0, 0.18), -2px 0 10px rgba(0, 0, 0, 0.07);
}

.all-imgs-container img {
  width: 100%;
  display: block;
}

.all-imgs-item {
  position: relative;
  border: 1px solid #e2e8f0;
}

.all-imgs-item + .all-imgs-item {
  border-top: none;
}

/* ─── Spinner ─── */

.spinner {
  width: 40px;
  height: 40px;
  position: relative;
}

.double-bounce1,
.double-bounce2 {
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background-color: #137fec;
  opacity: 0.6;
  position: absolute;
  top: 0;
  left: 0;
  animation: sk-bounce 2s infinite ease-in-out;
}

.double-bounce2 {
  animation-delay: -1s;
}

@keyframes sk-bounce {
  0%, 100% { transform: scale(0); }
  50% { transform: scale(1); }
}
</style>
